import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import BottomCTA from "@/components/site/BottomCTA";
import useReveal from "@/hooks/useReveal";
import { findCompany } from "@/data/content";
import { pickLocale } from "@/i18n";

export default function CompanyDetail() {
  useReveal();
  const { t } = useTranslation();
  const { slug } = useParams();
  const company = findCompany(slug);

  if (!company) {
    return (
      <div className="min-h-screen bg-white text-unik-ink">
        <SiteNav />
        <div className="mx-auto max-w-[1200px] px-6 py-40 text-center">
          <h1 className="font-display text-4xl text-[#14355a]">{t("companyDetail.notFound")}</h1>
          <Link to="/about/portfolio-companies" className="link-rule mt-8 inline-block text-[#14355a] text-sm">{t("companyDetail.backToAbout")}</Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const name = pickLocale(company, "name");
  const sector = pickLocale(company, "sector");
  const focus = pickLocale(company, "focus");
  const short = pickLocale(company, "short");
  const body = pickLocale(company, "body") || company.body;
  const keyAssets = pickLocale(company, "keyAssets") || company.keyAssets;
  const keyAssetsLabel = pickLocale(company, "keyAssetsLabel") || t("companyDetail.keyAssets");

  return (
    <div className="min-h-screen bg-white text-unik-ink">
      <SiteNav />

      <section className="relative h-[60vh] lg:h-[72vh] overflow-hidden bg-[#14355a]">
        <img src={company.img} alt={name} className="absolute inset-0 w-full h-full object-cover opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0e2540] via-[#0e2540]/60 to-[#14355a]/40" />
        <div className="relative z-10 mx-auto max-w-[1400px] px-6 lg:px-10 h-full flex flex-col justify-end pb-16 lg:pb-20 text-white">
          <Link to="/about/portfolio-companies" data-reveal className="inline-flex items-center gap-2 text-white/80 text-xs uppercase tracking-[0.2em] font-display hover:text-unik-gold transition-colors mb-8">
            <ArrowLeft size={14} /> {t("companyDetail.backToAbout")}
          </Link>
          <div data-reveal className="eyebrow text-unik-gold">{sector}</div>
          <h1
            data-reveal
            style={{ "--reveal-delay": "120ms", fontSize: "clamp(2.5rem, 6vw, 5rem)", fontWeight: 500, lineHeight: "1.0" }}
            className="mt-6 font-display tracking-tight"
          >
            {name}
          </h1>
          <p data-reveal style={{ "--reveal-delay": "240ms" }} className="mt-4 text-white/80 text-lg">
            {focus}
          </p>
        </div>
      </section>

      <section className="relative bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-20">
          <div className="lg:col-span-5" data-reveal>
            <div className="eyebrow text-[#14355a]">{t("companyDetail.overview")}</div>
            <h2
              style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)", fontWeight: 500, lineHeight: "1.0" }}
              className="mt-8 font-display text-[#14355a] tracking-tight"
            >
              {short}
            </h2>
            <div className="mt-10">
              <div className="eyebrow text-[#14355a]">{keyAssetsLabel}</div>
              <ul className="mt-5 space-y-3">
                {keyAssets.map((a) => (
                  <li key={a} className="text-[18px] lg:text-[20px] text-[#14355a] leading-relaxed font-display" style={{ fontWeight: 500 }}>— {a}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="lg:col-span-7 space-y-6 text-[16px] lg:text-[17px] leading-[1.95] text-unik-ink/80">
            {body.map((p, i) => (
              <p key={`body-${i}-${p.slice(0, 24)}`} data-reveal style={{ "--reveal-delay": `${i * 100}ms` }}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      <BottomCTA
        eyebrow={t("companyDetail.ctaEyebrow")}
        title={t("companyDetail.ctaTitle")}
        accent={t("companyDetail.ctaAccent")}
        body={t("companyDetail.ctaBody")}
        primary={{ label: t("common.contactUs"), to: "/contact" }}
        secondary={{ label: t("companyDetail.ctaSecondary"), to: "/investments/approach" }}
      />
      <SiteFooter />
    </div>
  );
}
